import { useAuthStore } from '@/stores/auth';

// Analytics are only active when the corresponding env var is set at build
// time. Local dev .env leaves these empty so dev sessions are not recorded;
// production values live in the server-managed .env on the prod host.
const GA_MEASUREMENT_ID = import.meta.env.VITE_GA_MEASUREMENT_ID;
const CLARITY_PROJECT_ID = import.meta.env.VITE_CLARITY_PROJECT_ID;

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
    clarity?: { (...args: unknown[]): void; q?: IArguments[] };
  }
}

function loadScript(src: string): void {
  const script = document.createElement('script');
  script.async = true;
  script.src = src;
  document.head.appendChild(script);
}

function initGoogleAnalytics(): void {
  window.dataLayer = window.dataLayer || [];
  // gtag.js requires the raw `arguments` object to be pushed, not an array
  window.gtag = function gtag() {
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer!.push(arguments);
  };
  window.gtag('js', new Date());
  window.gtag('config', GA_MEASUREMENT_ID);
  loadScript(`https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`);
}

function initClarity(): void {
  window.clarity =
    window.clarity ||
    function () {
      // eslint-disable-next-line prefer-rest-params
      (window.clarity!.q = window.clarity!.q || []).push(arguments);
    };
  loadScript(`https://www.clarity.ms/tag/${CLARITY_PROJECT_ID}`);
}

// Tie analytics sessions to the logged-in user. Subscribing to the auth
// store covers every path that changes the user: email login, register,
// Google OAuth callback, session restore on refresh, and logout.
function bindUserIdentity(): void {
  let identifiedUserId: string | null = null;

  useAuthStore.subscribe((state) => {
    const userId = state.user?.id ?? null;
    if (userId === identifiedUserId) return;
    identifiedUserId = userId;

    if (userId) {
      window.gtag?.('set', { user_id: userId });
      window.clarity?.('identify', userId, undefined, undefined, state.user?.email);
    } else {
      window.gtag?.('set', { user_id: null });
    }
  });
}

export function initAnalytics(): void {
  if (GA_MEASUREMENT_ID) initGoogleAnalytics();
  if (CLARITY_PROJECT_ID) initClarity();
  if (GA_MEASUREMENT_ID || CLARITY_PROJECT_ID) bindUserIdentity();
}
